import React, { useEffect, useId, useRef, useState } from 'react';
import Modal from '../../components/Modal/Modal';
import { updateReminderRule } from '../../api/reminders';
import { useI18n } from '../../i18n/I18nContext';
import { errorText } from '../../utils/errorText';
import ChannelPicker from './composer/ChannelPicker';
import LinkFields from './composer/LinkFields';
import MessageField from './composer/MessageField';
import useComposerOptions from './composer/useComposerOptions';
import {
  CHANNELS, MESSAGE_MAX, PROMO_REDEEMABLE, ruleMeta,
} from './reminderUi';

/**
 * Edit one automatic reminder: its text (with the placeholder chips and a way back to the standard
 * text), the channels, the SMS fallback and, optionally, the course its link opens and a promo
 * code. Mounted only while open.
 *
 *   {rule && <RuleEditModal rule={rule} onClose={close} onSaved={(saved) => replace(saved)} />}
 *
 * The text equal to the standard one is saved as '' (the server then follows the standard text);
 * a course or promo field that the login cannot read (or, for the promo, that is switched off while
 * PROMO_REDEEMABLE is false) is left out of the request, so it never wipes a value it could not show.
 */
export default function RuleEditModal({ rule, onClose, onSaved }) {
  const { t } = useI18n();
  const uid = useId();
  const options = useComposerOptions();
  const standard = String(rule.defaultMessage || '');
  const initial = {
    message: String(rule.message || standard),
    channels: CHANNELS.filter((key) => key === 'IN_APP' || (rule.channels || []).includes(key)),
    smsFallbackOnly: rule.smsFallbackOnly !== false,
    courseId: rule.courseId || '',
    promoId: rule.discountId || '',
  };
  const [message, setMessage] = useState(initial.message);
  const [channels, setChannels] = useState(initial.channels);
  const [smsFallback, setSmsFallback] = useState(initial.smsFallbackOnly);
  const [courseId, setCourseId] = useState(initial.courseId);
  const [promoId, setPromoId] = useState(initial.promoId);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [messageError, setMessageError] = useState('');
  const alive = useRef(true);
  // React's StrictMode runs this effect, its cleanup and the effect again in development: the flag
  // must come back to true, or every later `if (!alive.current)` would swallow the result of a save.
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  const dirty = message !== initial.message
    || channels.join() !== initial.channels.join()
    || smsFallback !== initial.smsFallbackOnly
    || courseId !== initial.courseId
    || promoId !== initial.promoId;

  const meta = ruleMeta(rule.key);
  const known = meta.audience !== null;
  const title = known ? t(`reminders.rules.items.${rule.key}.title`) : rule.key;

  const submit = async () => {
    const text = message.trim();
    if (!text) {
      setMessageError(t('reminders.compose.messageRequired'));
      return;
    }
    setMessageError('');
    setSaving(true);
    setError('');
    try {
      const saved = await updateReminderRule(rule.key, {
        message: text === standard.trim() ? '' : text,
        channels,
        smsFallbackOnly: smsFallback,
        ...(options.courses.length > 0 ? { courseId: courseId || null } : {}),
        ...(PROMO_REDEEMABLE && options.promos.length > 0 ? { discountId: promoId || null } : {}),
      });
      if (alive.current) onSaved(saved && saved.key ? saved : null);
    } catch (err) {
      if (alive.current) setError(errorText(err, t('reminders.rules.saveError')));
    } finally {
      if (alive.current) setSaving(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={t('reminders.rules.editTitle')}
      description={title}
      size="lg"
      closeOnBackdrop={!dirty}
      as="form"
      onSubmit={submit}
      formProps={{ noValidate: true }}
      footer={(
        <>
          <button type="button" className="ds-btn ds-btn-secondary" onClick={onClose}>{t('reminders.rules.cancel')}</button>
          <button type="submit" className="ds-btn ds-btn-primary" disabled={saving}>{t('reminders.rules.save')}</button>
        </>
      )}
    >
      <div className="ui-stack">
        <p className="rem-rule-edit__rule">{title}</p>

        <MessageField
          id={`${uid}-message`}
          label={t('reminders.message.textLabel')}
          value={message}
          onChange={setMessage}
          error={messageError}
          maxLength={MESSAGE_MAX}
          smsSelected={channels.includes('SMS')}
          onReset={() => { setMessage(standard); setMessageError(''); }}
          resetDisabled={message === standard}
        />

        <ChannelPicker
          value={channels}
          onChange={setChannels}
          channels={options.channels}
          smsFallbackOnly={smsFallback}
          onSmsFallbackChange={setSmsFallback}
        />

        <LinkFields
          courses={options.courses}
          promos={options.promos}
          courseId={courseId}
          onCourseChange={setCourseId}
          promoId={promoId}
          onPromoChange={setPromoId}
        />

        {error && <p className="ui-error" role="alert">{error}</p>}
      </div>
    </Modal>
  );
}
